import { useState, useEffect } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card'
import { Button } from '../ui/button'
import { Alert, AlertDescription } from '../ui/alert'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'
import { AlertCircle, Palette, Save, Package } from 'lucide-react'
import { TemplateAndLayoutView } from './TemplateAndLayoutView'
import { conversationStyleSettingsAPI } from '../../services/api'
import { messageService } from '../../services/messageService'
import { COLOR_PALETTES } from './misc'

interface StyleSettingsProps {
  conversationId?: string | null
}

export function StyleSettings({ conversationId }: StyleSettingsProps) {
  const [settings, setSettings] = useState({
    selectedPalette: 'default',
    customColors: '',
  })
  
  const [hasColorChanges, setHasColorChanges] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [backendState, setBackendState] = useState<{ selectedPalette: string; customColors: string } | null>(null)

  // Load saved settings
  useEffect(() => {
    if (conversationId) {
      conversationStyleSettingsAPI.get(conversationId)
        .then((response) => {
          if (response.success && response.data) {
            const { selectedPalette, customColors } = response.data
            const loadedState = {
              selectedPalette: selectedPalette || 'default',
              customColors: customColors || ''
            }
            setBackendState(loadedState)
            setSettings(prev => ({
              ...prev,
              selectedPalette: loadedState.selectedPalette,
              customColors: loadedState.customColors
            }))
          }
        })
        .catch(console.error)
    }
  }, [conversationId])

  // Check for unsaved changes
  useEffect(() => {
    if (backendState) {
      const hasUnsavedChanges = (
        settings.selectedPalette !== backendState.selectedPalette ||
        settings.customColors !== backendState.customColors
      )
      setHasColorChanges(hasUnsavedChanges)
    }
  }, [settings, backendState])

  const handleChange = (key: string, value: any) => {
    setSettings(prev => ({ ...prev, [key]: value }))
  }

  const handleSaveColors = async () => {
    if (!conversationId || !hasColorChanges) return

    setIsSaving(true)
    try {
      // Get color scheme (either selected palette or custom colors)
      let colorScheme = ''
      if (settings.selectedPalette === 'custom') {
        colorScheme = settings.customColors.trim() || 'Custom colors (not specified)'
      } else {
        const selectedPaletteOption = COLOR_PALETTES.find(palette => palette.id === settings.selectedPalette)
        if (selectedPaletteOption) {
          colorScheme = `${selectedPaletteOption.name} (${selectedPaletteOption.colors.join(', ')})`
        } else {
          colorScheme = settings.selectedPalette
        }
      }

      await Promise.all([
        conversationStyleSettingsAPI.update(conversationId, {
          selectedStyle: 'custom',
          selectedPalette: settings.selectedPalette,
          customColors: settings.customColors
        }),
        messageService.sendMessage({
          conversationId: conversationId,
          content: `Update the color palette to: ${colorScheme}`
        })
      ])
      
      // Update backend state to reflect what was just saved
      setBackendState({
        selectedPalette: settings.selectedPalette,
        customColors: settings.customColors
      })
      setHasColorChanges(false)
    } catch (error) {
      console.error('Failed to save color settings:', error)
    } finally {
      setIsSaving(false)
    }
  }

  const selectedPalette = COLOR_PALETTES.find(p => p.id === settings.selectedPalette)

  return (
    <div className="space-y-6">
      {/* Unified Template & Layout View */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package className="h-4 w-4" />
            Template & Layouts
          </CardTitle>
          <CardDescription>
            Choose a template and preview its available layouts
          </CardDescription>
        </CardHeader>
        <CardContent>
          <TemplateAndLayoutView conversationId={conversationId} />
        </CardContent>
      </Card>

      {/* Color Palette */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-1.5 text-sm font-medium">
            <Palette className="h-3.5 w-3.5" />
            Color Palette
          </CardTitle>
          <CardDescription className="text-xs">
            Select colors that match your brand or preference
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2 pt-0">
          <div className="space-y-1">
            <Label htmlFor="palette" className="text-xs">Color Palette</Label>
            <Select value={settings.selectedPalette} onValueChange={(value) => handleChange('selectedPalette', value)}>
              <SelectTrigger id="palette" className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {COLOR_PALETTES.map(palette => (
                  <SelectItem key={palette.id} value={palette.id}>
                    <div className="flex items-center gap-2">
                      <div className="flex gap-1">
                        {palette.colors.slice(0, 3).map((color, idx) => (
                          <div
                            key={idx}
                            className="h-3 w-3 rounded-sm"
                            style={{ backgroundColor: color }}
                          />
                        ))}
                      </div>
                      <span className="text-xs">{palette.name}</span>
                    </div>
                  </SelectItem>
                ))}
                <SelectItem value="custom" className="text-xs">Custom Colors</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {selectedPalette && settings.selectedPalette !== 'custom' && (
            <div className="space-y-1">
              <div className="flex flex-wrap gap-1.5">
                {selectedPalette.colors.map((color, idx) => (
                  <div
                    key={idx}
                    className="h-8 w-8 rounded border shadow-sm"
                    style={{ backgroundColor: color }}
                    title={color}
                  />
                ))}
              </div>
              <p className="text-xs text-muted-foreground">
                {selectedPalette.name}
              </p>
            </div>
          )}

          {settings.selectedPalette === 'custom' && (
            <div className="space-y-1">
              <Label htmlFor="custom-colors" className="text-xs">Custom Colors</Label>
              <Input
                id="custom-colors"
                placeholder="e.g., #FF5733, #33FF57, #3357FF"
                value={settings.customColors}
                onChange={(e) => handleChange('customColors', e.target.value)}
                className="h-8 text-xs"
              />
              <p className="text-[10px] text-muted-foreground">
                Enter comma-separated hex color codes
              </p>
            </div>
          )}

          {/* Save Button */}
          {conversationId && (
            <div className="flex justify-end pt-2">
              <Button
                onClick={handleSaveColors}
                disabled={!hasColorChanges || isSaving}
                size="sm"
                className="gap-2"
              >
                <Save className="h-4 w-4" />
                {isSaving ? 'Saving...' : 'Apply Colors'}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Info Alert when no conversation */}
      {!conversationId && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Start a conversation to configure style settings for your project.
          </AlertDescription>
        </Alert>
      )}
    </div>
  )
}